import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import { useNavigate, useSearchParams } from "react-router-dom";

import PageTitle from "../../layouts/PageTitle";
import { selectUser, selectUserRole } from "../../../store/authSlice";
import {
  appApi,
  useGetAllStaffQuery,
  useGetEmployeeStaffReportsQuery,
  useGetMyBackdateGrantsQuery,
  useGetRealEstateTeamsQuery,
  useGetTechnicalEmployeesQuery,
} from "../../../store/appApi";
import {
  calculateCompletionPercent,
  createAccountantTask,
  createContact,
  createFieldSubWork,
  createMainWork,
  createRevenueTask,
  createSubWork,
  FIELD_STATUS_OPTIONS,
  formatDateLabel,
  getDailyStat,
  formatRoleLabel,
  getDefaultFormData,
  getFormTypeForRole,
  getMetricEntryValue,
  getNetplusEntry,
  getPeriodKey,
  isCustomerDealerEntryEmpty,
  LEAD_SOURCE_OPTIONS,
  NETPLUS_STATUS_FIELDS,
  NETPLUS_MGR_FIELDS,
  getNetplusMgrEntry,
  upsertNetplusMgrEntry,
  withNetplusMgrRunningTotals,
  normalizeFieldReport,
  normalizeUnitKey,
  pickActiveReport,
  PROPERTY_TYPE_OPTIONS,
  sortSubWorkUpdates,
  toDateInputValue,
  toLegacyFieldMirror,
  toMonthKey,
  upsertDailyStat,
  upsertMetricEntries,
  upsertNetplusEntry,
} from "./reportingConfig";
import {
  createStaffReportApi,
  getMasterCustomersApi,
  getVisitorsApi,
  updateStaffReportApi,
} from "../../../services/staffReporting";
import VisitorRowsEditor, {
  pendingVisitorRows,
  saveVisitorRows,
} from "./VisitorRowsEditor";

const darkPanel =
  "rounded-3xl border border-gray-200 bg-white text-gray-900 shadow-xl shadow-slate-200/70";

const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";

const emptyStatusFor = (formType) => {
  const options = {
    1: "Pending",
    2: "Pending",
    3: "Pending",
    4: "Pending",
    5: "Pending",
    6: "Pending",
  };
  return options[Number(formType)] || "Pending";
};

const CreateStaffReporting = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";
  const currentUser = useSelector(selectUser);
  const myUserId = String(currentUser?.localId || "");
  // Real Estate team head (any role): may add NEW reports for his members.
  // The teams list holds only the teams this user heads.
  const [searchParams] = useSearchParams();
  const { data: reTeamData } = useGetRealEstateTeamsQuery(
    {},
    { skip: isSuperAdmin },
  );
  const isREManager = (reTeamData?.teams || []).length > 0;
  // Non-admins can only add today's or yesterday's report — matches the
  // backend's isBeforeAllowedWindow guard in createStaffReport/updateStaffReport.
  // A super-admin can unlock specific past days for an employee; the earliest
  // unlocked day lowers the picker's minimum (backend still checks each date).
  const { data: myGrantData } = useGetMyBackdateGrantsQuery(undefined, {
    skip: isSuperAdmin,
  });
  // Exact days an employee may pick: today, yesterday, plus each unlocked past day. The
  // picker must not offer the days in between — the backend refuses them.
  const grantedDayKeys = useMemo(
    () =>
      new Set(
        (Array.isArray(myGrantData?.grants) ? myGrantData.grants : []).map(
          (g) => String(g.dateKey),
        ),
      ),
    [myGrantData],
  );
  const earliestPickableDate = useMemo(() => {
    if (isSuperAdmin) return undefined;
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 1);
    const keys = (Array.isArray(myGrantData?.grants) ? myGrantData.grants : [])
      .map((g) => String(g.dateKey))
      .filter((k) => /^\d{4}-\d{2}-\d{2}$/.test(k))
      .sort();
    if (keys.length) {
      const g = new Date(`${keys[0]}T00:00:00`);
      if (!Number.isNaN(g.getTime()) && g < d) return g;
    }
    return d;
  }, [isSuperAdmin, myGrantData]);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(
    searchParams.get("employeeId") || "",
  );
  const [formData, setFormData] = useState({});
  const [activeReport, setActiveReport] = useState(null);
  const [saving, setSaving] = useState(false);
  // formType 1 only — which unit the sheet is scoped to. "" = none picked yet,
  // "__new__" = typing a brand-new unit, otherwise a normalized existing-unit
  // key. On the Update flow the two dropdowns are Unit → Main Work; pickMainId
  // is the selected main work and every one of its sub-works is shown below.
  // subWorkDrafts holds each sub-work's pending daily update as
  // { text, people }, keyed by sub-work id.
  const [unitChoice, setUnitChoice] = useState("");
  const [pickMainId, setPickMainId] = useState("");
  const [subWorkDrafts, setSubWorkDrafts] = useState({});

  // Changing the Working Date must not wipe a loaded unit sheet, so the
  // formType-1 reset effect reads the date from here instead of depending on it.
  const selectedDateRef = useRef(selectedDate);
  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  const { data: staffData, isLoading: staffLoading } = useGetAllStaffQuery({
    page: 1,
    limit: 500,
  });
  const { data: technicalData } = useGetTechnicalEmployeesQuery();
  const { data: reportData, isFetching: reportFetching } =
    useGetEmployeeStaffReportsQuery(selectedEmployeeId, {
      skip: !selectedEmployeeId,
    });

  const reAllowedIds = useMemo(() => {
    const ids = new Set([myUserId]);
    (reTeamData?.teams || []).forEach((t) =>
      (t.members || []).forEach((m) => ids.add(String(m._id))),
    );
    return ids;
  }, [reTeamData, myUserId]);
  const employees = Array.isArray(staffData?.staff)
    ? staffData.staff.filter(
        (item) =>
          item?.status !== false &&
          (!isREManager || reAllowedIds.has(String(item._id))),
      )
    : [];
  // Manager adding for someone else: always a brand-new sheet, never an edit.
  const onBehalfMode =
    isREManager &&
    !!selectedEmployeeId &&
    String(selectedEmployeeId) !== myUserId;
  const technicalStaff = Array.isArray(technicalData?.staff)
    ? technicalData.staff
    : [];

  const selectedEmployee = employees.find(
    (item) => String(item._id) === String(selectedEmployeeId),
  );
  const formType = getFormTypeForRole(selectedEmployee?.role);
  const reportMonth = toMonthKey(selectedDate);

  // Tele Calling / Real Estate Sales — the employee's Master Customer roster,
  // shown read-only in the Contacts section so it's clear which customers the
  // report will pull in. Call logging / remarks happen on the Detail page.
  const [masterCustomers, setMasterCustomers] = useState([]);
  useEffect(() => {
    if ((formType !== 3 && formType !== 4) || !selectedEmployeeId) {
      setMasterCustomers([]);
      return;
    }
    let cancelled = false;
    getMasterCustomersApi(formType, selectedEmployeeId)
      .then((res) => {
        if (!cancelled)
          setMasterCustomers(
            Array.isArray(res?.data?.customers) ? res.data.customers : [],
          );
      })
      .catch(() => !cancelled && setMasterCustomers([]));
    return () => {
      cancelled = true;
    };
  }, [formType, selectedEmployeeId]);

  // Real Estate Sales — CP / Customer visitor rows for the Working Date. New
  // rows live here until saved; `dayVisitors` is what's already on the server
  // for that date (the full list is on the Visitors page).
  const [visitorRows, setVisitorRows] = useState([]);
  const [dayVisitors, setDayVisitors] = useState([]);
  const [savingVisitors, setSavingVisitors] = useState(false);
  const visitorDate = toDateInputValue(selectedDate);
  const visitorEmployeeParam =
    selectedEmployeeId && String(selectedEmployeeId) !== myUserId
      ? String(selectedEmployeeId)
      : "";
  const loadDayVisitors = React.useCallback(async () => {
    if (formType !== 4 || !selectedEmployeeId) {
      setDayVisitors([]);
      return;
    }
    try {
      const res = await getVisitorsApi({
        from: visitorDate,
        to: visitorDate,
        ...(visitorEmployeeParam ? { employeeId: visitorEmployeeParam } : {}),
      });
      setDayVisitors(
        Array.isArray(res?.data?.visitors) ? res.data.visitors : [],
      );
    } catch {
      setDayVisitors([]);
    }
  }, [formType, selectedEmployeeId, visitorDate, visitorEmployeeParam]);
  useEffect(() => {
    loadDayVisitors();
  }, [loadDayVisitors]);
  useEffect(() => {
    setVisitorRows([]);
  }, [selectedEmployeeId]);

  const handleSaveVisitors = async () => {
    setSavingVisitors(true);
    try {
      const saved = await saveVisitorRows({
        employeeId: visitorEmployeeParam,
        date: visitorDate,
        rows: visitorRows,
      });
      if (!saved.length) return;
      setVisitorRows([]);
      await loadDayVisitors();
      Swal.fire({
        icon: "success",
        title: "Visitors saved",
        text: `${saved.length} visitor${saved.length === 1 ? "" : "s"} added to the list.`,
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "warning",
        title: "Unable to save visitors",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Could not save the visitors.",
      });
    } finally {
      setSavingVisitors(false);
    }
  };

  // formType 1 — the employee's still-open unit sheets, one distinct entry per
  // unit name, used to populate the Unit picker on the Create/Update page.
  const existingUnits = useMemo(() => {
    const reports = Array.isArray(reportData?.reports)
      ? reportData.reports
      : [];
    const byKey = new Map();
    reports.forEach((report) => {
      if (Number(report?.formType) !== 1 || report?.isComplete) return;
      const name = report?.formData?.unitName || "";
      const key = normalizeUnitKey(name);
      if (!key || byKey.has(key)) return;
      byKey.set(key, { key, name, report });
    });
    return Array.from(byKey.values());
  }, [reportData]);

  const unitMode =
    unitChoice === "__new__" ? "new" : unitChoice === "" ? "none" : "existing";

  useEffect(() => {
    if (!formType) {
      setActiveReport(null);
      setFormData({});
      return;
    }

    // formType 1 is driven entirely by the Unit picker (see the dedicated
    // effects and handleUnitChange below), so the shared period-resume logic
    // must not touch it.
    if (formType === 1) return;

    const reports = Array.isArray(reportData?.reports)
      ? reportData.reports
      : [];
    const periodKey = getPeriodKey(
      formType,
      { month: reportMonth },
      selectedDate,
    );
    const existing = onBehalfMode
      ? null
      : pickActiveReport(reports, formType, periodKey);

    if (existing) {
      setActiveReport(existing);
      setFormData({
        ...getDefaultFormData(formType, existing.reportDate || selectedDate),
        ...existing.formData,
      });
      return;
    }

    setActiveReport(null);
    setFormData(getDefaultFormData(formType, selectedDate));
  }, [formType, reportData, selectedDate, reportMonth, onBehalfMode]);

  // formType 1 — reset to a blank unit sheet whenever the employee or role
  // changes. Deliberately NOT dependent on selectedDate: the Working Date is a
  // row-default only and changing it must never wipe a loaded unit sheet.
  useEffect(() => {
    if (formType !== 1) return;
    setActiveReport(null);
    setUnitChoice("");
    setPickMainId("");
    setSubWorkDrafts({});
    setFormData(getDefaultFormData(1, selectedDateRef.current));
  }, [formType, selectedEmployeeId]);

  // If this employee has no open unit sheets yet, jump straight to "new unit"
  // so the editor is usable without an extra click.
  useEffect(() => {
    if (
      formType === 1 &&
      !activeReport &&
      unitChoice === "" &&
      existingUnits.length === 0
    ) {
      setUnitChoice("__new__");
    }
  }, [formType, activeReport, unitChoice, existingUnits.length]);

  const resetUnitPickers = () => {
    setPickMainId("");
  };

  const handleEmployeeChange = (nextEmployeeId) => {
    setSelectedEmployeeId(nextEmployeeId);
    setActiveReport(null);
    setUnitChoice("");
    resetUnitPickers();
    setSubWorkDrafts({});
    setFormData({});
  };

  const handleStartNewSheet = () => {
    setActiveReport(null);
    setSubWorkDrafts({});
    resetUnitPickers();
    if (formType === 1) setUnitChoice("__new__");
    setFormData(getDefaultFormData(formType, selectedDate));
  };

  // formType 1 — pick a unit: "__new__"/"" start a blank sheet, an existing
  // key loads that unit's open sheet (folded from any legacy shape) so every
  // main work for the unit is edited in one place. On load, the Main Work
  // dropdown defaults to the unit's first main work.
  const handleUnitChange = (value) => {
    setUnitChoice(value);
    setSubWorkDrafts({});
    resetUnitPickers();

    if (value === "__new__" || value === "") {
      setActiveReport(null);
      setFormData(getDefaultFormData(1, selectedDate));
      return;
    }

    const match = existingUnits.find((unit) => unit.key === value);
    if (!match) {
      setActiveReport(null);
      setFormData(getDefaultFormData(1, selectedDate));
      return;
    }

    const loaded = normalizeFieldReport(match.report.formData);
    setActiveReport(match.report);
    setFormData({ ...getDefaultFormData(1, selectedDate), ...loaded });
    setPickMainId(loaded.mainWorks?.[0]?.id || "");
  };

  const selectMainWork = (mainId) => {
    if (mainId === "__add__") {
      const mainWork = createMainWork(toDateInputValue(selectedDate));
      setFormData((prev) => ({
        ...prev,
        mainWorks: [
          ...(Array.isArray(prev.mainWorks) ? prev.mainWorks : []),
          mainWork,
        ],
      }));
      setPickMainId(mainWork.id);
      return;
    }
    setPickMainId(mainId);
  };

  const updateMainWork = (mainIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      mainWorks: (Array.isArray(prev.mainWorks) ? prev.mainWorks : []).map(
        (mainWork, index) =>
          index === mainIndex ? { ...mainWork, [field]: value } : mainWork,
      ),
    }));
  };

  const addMainWork = () => {
    setFormData((prev) => ({
      ...prev,
      mainWorks: [
        ...(Array.isArray(prev.mainWorks) ? prev.mainWorks : []),
        createMainWork(toDateInputValue(selectedDate)),
      ],
    }));
  };

  const removeMainWork = (mainIndex) => {
    setFormData((prev) => ({
      ...prev,
      mainWorks: (Array.isArray(prev.mainWorks) ? prev.mainWorks : []).filter(
        (_, index) => index !== mainIndex,
      ),
    }));
  };

  const mutateSubWorks = (mainIndex, mutator) => {
    setFormData((prev) => ({
      ...prev,
      mainWorks: (Array.isArray(prev.mainWorks) ? prev.mainWorks : []).map(
        (mainWork, index) =>
          index === mainIndex
            ? {
                ...mainWork,
                subWorks: mutator(
                  Array.isArray(mainWork.subWorks) ? mainWork.subWorks : [],
                ),
              }
            : mainWork,
      ),
    }));
  };

  const addFieldSubWork = (mainIndex) =>
    mutateSubWorks(mainIndex, (rows) => [
      ...rows,
      createFieldSubWork(toDateInputValue(selectedDate)),
    ]);

  const removeFieldSubWork = (mainIndex, subIndex) =>
    mutateSubWorks(mainIndex, (rows) =>
      rows.filter((_, index) => index !== subIndex),
    );

  const updateFieldSubWork = (mainIndex, subIndex, field, value) =>
    mutateSubWorks(mainIndex, (rows) =>
      rows.map((row, index) =>
        index === subIndex ? { ...row, [field]: value } : row,
      ),
    );

  const subWorkDraft = (id) => subWorkDrafts[id] || { text: "", people: "" };

  const setSubWorkDraft = (id, patch) =>
    setSubWorkDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || { text: "", people: "" }), ...patch },
    }));

  // Append (never overwrite) a dated progress note + head-count to one
  // sub-work's history. A row needs at least a note or a people count.
  const appendSubWorkUpdate = (mainIndex, subIndex) => {
    const subWork = formData.mainWorks?.[mainIndex]?.subWorks?.[subIndex];
    const draftKey = subWork?.id;
    const draft = subWorkDraft(draftKey);
    const text = String(draft.text || "").trim();
    const peopleRaw = String(draft.people ?? "").trim();
    const peopleCount =
      peopleRaw === "" ? "" : Math.max(0, Number(peopleRaw) || 0);
    if (!text && peopleCount === "") return;
    const dateValue = toDateInputValue(selectedDate);
    mutateSubWorks(mainIndex, (rows) =>
      rows.map((row, index) =>
        index === subIndex
          ? {
              ...row,
              updatedRemarks: [
                ...(Array.isArray(row.updatedRemarks)
                  ? row.updatedRemarks
                  : []),
                { date: dateValue, text, peopleCount },
              ],
            }
          : row,
      ),
    );
    setSubWorkDrafts((prev) => ({
      ...prev,
      [draftKey]: { text: "", people: "" },
    }));
  };

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const updateArrayItem = (key, index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: (Array.isArray(prev[key]) ? prev[key] : []).map(
        (item, itemIndex) =>
          itemIndex === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const addArrayRow = (key, rowFactory) => {
    setFormData((prev) => ({
      ...prev,
      [key]: [...(Array.isArray(prev[key]) ? prev[key] : []), rowFactory()],
    }));
  };

  const removeArrayRow = (key, index) => {
    setFormData((prev) => ({
      ...prev,
      [key]: (Array.isArray(prev[key]) ? prev[key] : []).filter(
        (_, itemIndex) => itemIndex !== index,
      ),
    }));
  };

  // Revenue tasks can carry their own nested sub-works (same shape as Field
  // Execution Journal's subWorks), so these mirror addArrayRow/removeArrayRow
  // but scoped to a single task instead of the top-level formData.
  const addTaskSubWork = (taskIndex) => {
    setFormData((prev) => ({
      ...prev,
      tasks: (Array.isArray(prev.tasks) ? prev.tasks : []).map((task, index) =>
        index === taskIndex
          ? {
              ...task,
              subWorks: [
                ...(Array.isArray(task.subWorks) ? task.subWorks : []),
                createSubWork(toDateInputValue(selectedDate)),
              ],
            }
          : task,
      ),
    }));
  };

  const updateTaskSubWork = (taskIndex, subIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      tasks: (Array.isArray(prev.tasks) ? prev.tasks : []).map((task, index) =>
        index === taskIndex
          ? {
              ...task,
              subWorks: (Array.isArray(task.subWorks) ? task.subWorks : []).map(
                (subWork, subWorkIndex) =>
                  subWorkIndex === subIndex
                    ? { ...subWork, [field]: value }
                    : subWork,
              ),
            }
          : task,
      ),
    }));
  };

  const removeTaskSubWork = (taskIndex, subIndex) => {
    setFormData((prev) => ({
      ...prev,
      tasks: (Array.isArray(prev.tasks) ? prev.tasks : []).map((task, index) =>
        index === taskIndex
          ? {
              ...task,
              subWorks: (Array.isArray(task.subWorks)
                ? task.subWorks
                : []
              ).filter((_, subWorkIndex) => subWorkIndex !== subIndex),
            }
          : task,
      ),
    }));
  };

  const updateMetricValue = (metricKey, value) => {
    const dateValue = toDateInputValue(selectedDate);
    setFormData((prev) => ({
      ...prev,
      metrics: upsertMetricEntries(prev.metrics, dateValue, {
        [metricKey]: value,
      }),
    }));
  };

  // Netplus Updates (formType 7) — write one status count for one area on the
  // selected date, keeping that area's other four counts for the day intact.
  const updateNetplusValue = (code, fieldKey, value) => {
    const dateValue = toDateInputValue(selectedDate);
    setFormData((prev) => {
      const areas = Array.isArray(prev.areas) ? prev.areas : [];
      const area = areas.find((a) => String(a.code) === String(code));
      const current = getNetplusEntry(area, dateValue);
      const valuesByKey = {};
      NETPLUS_STATUS_FIELDS.forEach(({ key }) => {
        valuesByKey[key] = key === fieldKey ? value : (current[key] ?? "");
      });
      return {
        ...prev,
        areas: upsertNetplusEntry(areas, dateValue, code, valuesByKey),
      };
    });
  };

  // Netplus Manager Daily (formType 8) — write the six figures for the
  // selected day; other days stay as they were.
  const updateNetplusMgrValue = (fieldKey, value) => {
    const dateValue = toDateInputValue(selectedDate);
    setFormData((prev) => ({
      ...prev,
      entries: upsertNetplusMgrEntry(prev.entries, dateValue, {
        [fieldKey]: String(value).replace(/[^0-9]/g, ""),
      }),
    }));
  };

  const addMetricRow = () => {
    setFormData((prev) => ({
      ...prev,
      metrics: [
        ...(Array.isArray(prev.metrics) ? prev.metrics : []),
        {
          key: `custom-${Date.now()}`,
          label: "",
          entries: [],
          isCustom: true,
        },
      ],
    }));
  };

  const updateMetricLabel = (metricKey, label) => {
    setFormData((prev) => ({
      ...prev,
      metrics: (Array.isArray(prev.metrics) ? prev.metrics : []).map(
        (metric) => (metric.key === metricKey ? { ...metric, label } : metric),
      ),
    }));
  };

  const removeMetricRow = (metricKey) => {
    setFormData((prev) => ({
      ...prev,
      metrics: (Array.isArray(prev.metrics) ? prev.metrics : []).filter(
        (metric) => metric.key !== metricKey,
      ),
    }));
  };

  const buildPayload = () => {
    const payload = {
      employeeId: selectedEmployeeId,
      formType,
      reportDate: selectedDate.toISOString(),
      date: selectedDate.toISOString(),
      status: formData.status || emptyStatusFor(formType),
      remarks: formData.remarks || "",
      ...formData,
    };

    if (formType === 1) {
      payload.unitName = String(formData.unitName || "").trim();
      // Flat legacy mirror so the not-yet-migrated Detail/List/Excel views keep
      // rendering formType-1 sheets alongside the new mainWorks tree.
      Object.assign(payload, toLegacyFieldMirror(formData));
    }
    if (
      formType === 2 ||
      formType === 3 ||
      formType === 4 ||
      formType === 7 ||
      formType === 8
    ) {
      payload.month = formData.month || reportMonth;
    }
    if (formType === 4) {
      payload.customerDealerLog = (
        Array.isArray(formData.customerDealerLog)
          ? formData.customerDealerLog
          : []
      ).filter((row) => !isCustomerDealerEntryEmpty(row));
    }
    return payload;
  };

  const handleFormKeyDown = (e) => {
    if (
      e.key === "Enter" &&
      e.target.tagName !== "TEXTAREA" &&
      e.target.type !== "submit"
    ) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEmployeeId || !formType) {
      Swal.fire({
        icon: "warning",
        title: "Select employee",
        text: "Please choose an active employee with a supported reporting role.",
      });
      return;
    }

    if (!formData) return;

    if (formType === 1 && !String(formData.unitName || "").trim()) {
      Swal.fire({
        icon: "warning",
        title: "Unit required",
        text: "Enter or select a unit before saving this sheet.",
      });
      return;
    }

    if (formType === 4) {
      try {
        pendingVisitorRows(visitorRows);
      } catch (error) {
        Swal.fire({
          icon: "warning",
          title: "Check visitors",
          text: error.message,
        });
        return;
      }
    }

    try {
      setSaving(true);
      const payload = buildPayload();
      const response =
        activeReport?._id && !onBehalfMode
          ? await updateStaffReportApi(activeReport._id, payload)
          : await createStaffReportApi(payload);

      dispatch(appApi.util.invalidateTags(["WorkProgressReports", "Staff"]));
      // Real Estate Sales — the visitor rows typed on this form go to the
      // employee's separate visitor list alongside the report save.
      if (formType === 4 && pendingVisitorRows(visitorRows).length) {
        await saveVisitorRows({
          employeeId: visitorEmployeeParam,
          date: visitorDate,
          rows: visitorRows,
        });
        setVisitorRows([]);
        loadDayVisitors();
      }
      const report = response?.data?.report;
      if (report && onBehalfMode) {
        // Saved as a new sheet in the member's name, stamped "Added by <me>".
        // Keep it out of the editor so it can't be edited from here.
        setActiveReport(null);
        setFormData(getDefaultFormData(formType, selectedDate));
        await Swal.fire({
          icon: "success",
          title: "Report added",
          text: `Added for ${selectedEmployee?.name || "the team member"}. It shows as added by you.`,
          timer: 2200,
          showConfirmButton: false,
        });
        navigate(`/staff-reporting/employee/${selectedEmployeeId}`);
        return;
      }
      if (report) {
        setActiveReport(report);
        // Keep the Unit picker pointed at the sheet we just saved so a follow-up
        // edit stays on the same unit (Update cascade) instead of "new". Point
        // the cascade at the first main work / sub-work so it's not blank.
        if (formType === 1) {
          setUnitChoice(
            normalizeUnitKey(report.formData?.unitName || formData.unitName),
          );
          setPickMainId((formData.mainWorks || [])[0]?.id || "");
        }
        // Tele Calling / Real Estate Sales — blank the entry inputs after a
        // save so the next day's / next batch's values start fresh. The saved
        // data lives on the server and merges on the next Update.
        if (formType === 3 || formType === 4) {
          setFormData((prev) => ({
            ...prev,
            dailyStats: [],
            contacts: [],
            metrics: Array.isArray(prev.metrics)
              ? prev.metrics.map((m) => ({ ...m, entries: [] }))
              : prev.metrics,
          }));
        }
      }

      Swal.fire({
        icon: "success",
        title: "Saved",
        text: activeReport?._id
          ? "The reporting sheet was updated."
          : "The reporting sheet was created.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text:
          error?.response?.data?.message ||
          "Something went wrong while saving this report.",
      });
    } finally {
      setSaving(false);
    }
  };

  const completion = calculateCompletionPercent(
    formType,
    formData,
    formData.status || emptyStatusFor(formType),
  );

  // ─── formType 1 — Field Execution Journal ──────────────────────────────────
  // One sheet per Unit. The Unit holds every Main Work; each Main Work holds
  // its own Sub Works; each Sub Work carries dates, status, a remark and an
  // append-only daily-update log (newest first).
  const renderFieldSubWork = (mainWork, mainIndex, subWork, subIndex) => {
    const updates = sortSubWorkUpdates(subWork.updatedRemarks, {
      newestFirst: true,
    });
    const draft = subWorkDraft(subWork.id);
    return (
      <div
        key={subWork.id || subIndex}
        className="rounded-xl border border-gray-200 bg-gray-50 p-3"
      >
        <div className="grid gap-2 md:grid-cols-4">
          <div className="md:col-span-2">
            <label className={labelClass}>Sub-work</label>
            <input
              className={inputClass}
              autoComplete="off"
              value={subWork.subWorkName || ""}
              onChange={(event) =>
                updateFieldSubWork(
                  mainIndex,
                  subIndex,
                  "subWorkName",
                  event.target.value,
                )
              }
              placeholder="Conduiting / Wiring / Testing"
            />
          </div>
          <div>
            <label className={labelClass}>Start Date</label>
            <input
              type="date"
              className={inputClass}
              autoComplete="off"
              value={subWork.startDate || ""}
              onChange={(event) =>
                updateFieldSubWork(
                  mainIndex,
                  subIndex,
                  "startDate",
                  event.target.value,
                )
              }
            />
          </div>
          <div>
            <label className={labelClass}>Completion Date</label>
            <input
              type="date"
              className={inputClass}
              autoComplete="off"
              value={subWork.completionDate || ""}
              onChange={(event) =>
                updateFieldSubWork(
                  mainIndex,
                  subIndex,
                  "completionDate",
                  event.target.value,
                )
              }
            />
            {subWork.extendedDate ? (
              <small className="mt-1 block text-xs font-semibold text-sky-600">
                Extended to {formatDateLabel(subWork.extendedDate)}
                {subWork.extensionReason ? ` — ${subWork.extensionReason}` : ""}
              </small>
            ) : null}
          </div>
          <div>
            <label className={labelClass}>Status</label>
            <select
              className={inputClass}
              autoComplete="off"
              value={subWork.status || "Pending"}
              onChange={(event) =>
                updateFieldSubWork(
                  mainIndex,
                  subIndex,
                  "status",
                  event.target.value,
                )
              }
            >
              {FIELD_STATUS_OPTIONS.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className={labelClass}>Remarks</label>
            <input
              className={inputClass}
              autoComplete="off"
              value={subWork.remarks || ""}
              onChange={(event) =>
                updateFieldSubWork(
                  mainIndex,
                  subIndex,
                  "remarks",
                  event.target.value,
                )
              }
              placeholder="Any remark for this sub-work"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              className="w-full rounded-xl border-2 border-amber-500 px-3 py-2 text-xs font-semibold text-gray-600 hover:text-amber-700"
              onClick={() => removeFieldSubWork(mainIndex, subIndex)}
            >
              Remove
            </button>
          </div>
        </div>

        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
          <label className={labelClass}>
            Daily Update — {formatDateLabel(selectedDate)}
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="number"
              min="0"
              className={`${inputClass} sm:w-40`}
              autoComplete="off"
              value={draft.people}
              onChange={(event) =>
                setSubWorkDraft(subWork.id, { people: event.target.value })
              }
              placeholder="People on work"
            />
            <input
              className={inputClass}
              autoComplete="off"
              value={draft.text}
              onChange={(event) =>
                setSubWorkDraft(subWork.id, { text: event.target.value })
              }
              placeholder="Progress note for today"
            />
            <button
              type="button"
              className="rounded-xl bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-950 transition hover:bg-amber-400"
              onClick={() => appendSubWorkUpdate(mainIndex, subIndex)}
            >
              Add Update
            </button>
          </div>
          {updates.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-gray-500">
              {updates.map((entry, entryIndex) => (
                <li key={`${entry.date}-${entryIndex}`}>
                  <span className="font-semibold text-gray-700">
                    {formatDateLabel(entry.date)}:
                  </span>{" "}
                  {entry.peopleCount !== "" && (
                    <span className="font-medium text-gray-600">
                      👥 {entry.peopleCount}
                      {entry.text ? " — " : ""}
                    </span>
                  )}
                  {entry.text}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderMainWorkFields = (mainWork, mainIndex) => (
    <div className="grid gap-3 md:grid-cols-4">
      <div className="md:col-span-2">
        <label className={labelClass}>Main Work</label>
        <input
          className={inputClass}
          autoComplete="off"
          value={mainWork.mainWorkName || ""}
          onChange={(event) =>
            updateMainWork(mainIndex, "mainWorkName", event.target.value)
          }
          placeholder="Electrical / Plumbing / Civil / Finishing"
        />
      </div>
      <div>
        <label className={labelClass}>Start Date</label>
        <input
          type="date"
          className={inputClass}
          autoComplete="off"
          value={mainWork.startDate || ""}
          onChange={(event) =>
            updateMainWork(mainIndex, "startDate", event.target.value)
          }
        />
      </div>
      <div>
        <label className={labelClass}>Completion Date</label>
        <input
          type="date"
          className={inputClass}
          autoComplete="off"
          value={mainWork.completionDate || ""}
          onChange={(event) =>
            updateMainWork(mainIndex, "completionDate", event.target.value)
          }
        />
        {mainWork.extendedDate ? (
          <small className="mt-1 block text-xs font-semibold text-sky-600">
            Extended to {formatDateLabel(mainWork.extendedDate)}
            {mainWork.extensionReason ? ` — ${mainWork.extensionReason}` : ""}
          </small>
        ) : null}
      </div>
      <div>
        <label className={labelClass}>Status</label>
        <select
          className={inputClass}
          autoComplete="off"
          value={mainWork.status || "Pending"}
          onChange={(event) =>
            updateMainWork(mainIndex, "status", event.target.value)
          }
        >
          {FIELD_STATUS_OPTIONS.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
      </div>
    </div>
  );

  // "New unit" mode — one expandable card per main work with all its sub-works.
  const renderMainWorkCard = (mainWork, mainIndex) => (
    <div
      key={mainWork.id || mainIndex}
      className="rounded-2xl border border-gray-200 bg-gray-50 p-4"
    >
      {renderMainWorkFields(mainWork, mainIndex)}

      <div className="mt-4 rounded-xl border border-gray-200 bg-white p-3">
        <div className="flex items-center justify-between gap-3">
          <label className={labelClass}>
            Sub Works under “{mainWork.mainWorkName || "this work"}”
          </label>
          <button
            type="button"
            className="rounded-lg bg-amber-500 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-950 transition hover:bg-amber-400"
            onClick={() => addFieldSubWork(mainIndex)}
          >
            Add Sub-work
          </button>
        </div>
        {(Array.isArray(mainWork.subWorks) ? mainWork.subWorks : []).length ? (
          <div className="mt-3 space-y-3">
            {mainWork.subWorks.map((subWork, subIndex) =>
              renderFieldSubWork(mainWork, mainIndex, subWork, subIndex),
            )}
          </div>
        ) : (
          <p className="mt-2 text-xs text-gray-400">No sub-works yet.</p>
        )}
        <button
          type="button"
          className="mt-3 w-full rounded-xl border-2 border-dashed border-amber-500 px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 transition hover:bg-amber-50"
          onClick={() => addFieldSubWork(mainIndex)}
        >
          + Add more sub-work
        </button>
      </div>

      <div className="mt-3 text-right">
        <button
          type="button"
          className="rounded-xl border-2 border-amber-500 px-3 py-2 text-xs font-semibold text-gray-600 hover:text-amber-700"
          onClick={() => removeMainWork(mainIndex)}
        >
          Remove Main Work
        </button>
      </div>
    </div>
  );

  // "Update" mode — the selected main work's fields + every one of its
  // sub-works shown at once (each with its daily update + head-count).
  const renderSelectedMainWork = (mainWorks) => {
    if (unitMode !== "existing") {
      return (
        <div className="rounded-2xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          Choose a unit above to load its main works, or pick “➕ New unit…” to
          start a fresh sheet.
        </div>
      );
    }

    const mainIndex = mainWorks.findIndex(
      (mainWork) => mainWork.id === pickMainId,
    );
    const mainWork = mainIndex >= 0 ? mainWorks[mainIndex] : null;

    if (!mainWork) {
      return (
        <div className="space-y-3 rounded-2xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          <p>
            Select a main work for {formData.unitName || "this unit"} to see its
            sub-works — or add another one.
          </p>
          <button
            type="button"
            className="rounded-xl bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-950 transition hover:bg-amber-400"
            onClick={() => selectMainWork("__add__")}
          >
            + Add more main work
          </button>
        </div>
      );
    }

    const subWorks = Array.isArray(mainWork.subWorks) ? mainWork.subWorks : [];

    return (
      <div className="space-y-4 rounded-2xl border border-gray-200 bg-gray-50 p-4">
        {renderMainWorkFields(mainWork, mainIndex)}
        <div className="text-right">
          <button
            type="button"
            className="rounded-xl border-2 border-amber-500 px-3 py-2 text-xs font-semibold text-gray-600 hover:text-amber-700"
            onClick={() => {
              removeMainWork(mainIndex);
              setPickMainId("");
            }}
          >
            Remove Main Work
          </button>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="flex items-center justify-between gap-3">
            <label className={labelClass}>
              Sub Works under “{mainWork.mainWorkName || "this work"}”
            </label>
            <button
              type="button"
              className="rounded-lg bg-amber-500 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-950 transition hover:bg-amber-400"
              onClick={() => addFieldSubWork(mainIndex)}
            >
              Add Sub-work
            </button>
          </div>
          {subWorks.length ? (
            <div className="mt-3 space-y-3">
              {subWorks.map((subWork, subIndex) =>
                renderFieldSubWork(mainWork, mainIndex, subWork, subIndex),
              )}
            </div>
          ) : (
            <p className="mt-2 text-xs text-gray-400">No sub-works yet.</p>
          )}
          <button
            type="button"
            className="mt-3 w-full rounded-xl border-2 border-dashed border-amber-500 px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 transition hover:bg-amber-50"
            onClick={() => addFieldSubWork(mainIndex)}
          >
            + Add more sub-work
          </button>
        </div>

        <button
          type="button"
          className="w-full rounded-xl border-2 border-dashed border-amber-500 px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 transition hover:bg-amber-50"
          onClick={() => selectMainWork("__add__")}
        >
          + Add more main work
        </button>
      </div>
    );
  };

  const renderFieldJournal = () => {
    const mainWorks = Array.isArray(formData.mainWorks)
      ? formData.mainWorks
      : [];
    const isNew = unitMode === "new";

    return (
      <div className="space-y-6">
        {/* Dropdown 1: Unit — Dropdown 2: Main Work (or New Unit Name) */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Unit</label>
            <select
              className={inputClass}
              autoComplete="off"
              value={unitChoice}
              onChange={(event) => handleUnitChange(event.target.value)}
            >
              <option value="">Select a unit…</option>
              {existingUnits.map((unit) => (
                <option key={unit.key} value={unit.key}>
                  {unit.name}
                </option>
              ))}
              <option value="__new__">➕ New unit…</option>
            </select>
          </div>

          {isNew ? (
            <div>
              <label className={labelClass}>New Unit Name</label>
              <input
                className={inputClass}
                autoComplete="off"
                value={formData.unitName || ""}
                onChange={(event) =>
                  updateField("unitName", event.target.value)
                }
                placeholder="Villa 12 / Plot 7 / Block A-3"
              />
            </div>
          ) : (
            <div>
              <label className={labelClass}>Main Work</label>
              <select
                className={inputClass}
                autoComplete="off"
                value={pickMainId}
                disabled={unitMode !== "existing"}
                onChange={(event) => selectMainWork(event.target.value)}
              >
                <option value="">
                  {unitMode === "existing"
                    ? "Select main work…"
                    : "Select a unit first"}
                </option>
                {mainWorks.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.mainWorkName || "Untitled main work"}
                  </option>
                ))}
                {unitMode === "existing" && (
                  <option value="__add__">➕ Add main work…</option>
                )}
              </select>
            </div>
          )}
        </div>

        {isNew ? (
          <>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">
                  Main Works
                </h4>
                <p className="text-xs text-gray-400">
                  Every main work for {formData.unitName || "this unit"} lives
                  on this one sheet — add as many as needed.
                </p>
              </div>
              <button
                type="button"
                className="rounded-xl bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-950 transition hover:bg-amber-400"
                onClick={addMainWork}
              >
                + Add more main work
              </button>
            </div>

            <div className="space-y-4">
              {mainWorks.map((mainWork, mainIndex) =>
                renderMainWorkCard(mainWork, mainIndex),
              )}
            </div>

            <button
              type="button"
              className="w-full rounded-xl border-2 border-dashed border-amber-500 px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 transition hover:bg-amber-50"
              onClick={addMainWork}
            >
              + Add more main work
            </button>
          </>
        ) : (
          renderSelectedMainWork(mainWorks)
        )}
      </div>
    );
  };

  const renderMetricsForm = (title, description) => {
    const metrics = Array.isArray(formData.metrics) ? formData.metrics : [];
    const dateValue = toDateInputValue(selectedDate);
    // Office Staff and Real Estate Sales get an inline Date field next to
    // Month so backdating a day's values doesn't require scrolling up to the
    // page-level Working Date picker.
    const showInlineDatePicker =
      formType === 2 || formType === 3 || formType === 4;
    return (
      <div className="space-y-5">
        <div
          className={
            showInlineDatePicker ? "grid gap-4 sm:grid-cols-2" : undefined
          }
        >
          <div>
            <label className={labelClass}>Month</label>
            <input
              type="month"
              className={inputClass}
              autoComplete="off"
              value={formData.month || reportMonth}
              onChange={(event) => updateField("month", event.target.value)}
            />
          </div>
          {showInlineDatePicker && (
            <div>
              <label className={labelClass}>Date</label>
              <input
                type="date"
                className={inputClass}
                autoComplete="off"
                value={dateValue}
                max={toDateInputValue()}
                min={
                  earliestPickableDate
                    ? toDateInputValue(earliestPickableDate)
                    : undefined
                }
                onChange={(event) => {
                  if (!event.target.value) return;
                  const next = new Date(`${event.target.value}T00:00:00`);
                  if (!Number.isNaN(next.getTime())) setSelectedDate(next);
                }}
              />
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">
              {title}
            </h4>
            <p className="text-xs text-gray-400">
              {description} Values below are saved for{" "}
              {formatDateLabel(selectedDate)}.
            </p>
          </div>
          <button
            type="button"
            className="rounded-xl border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600 transition hover:text-amber-700"
            onClick={addMetricRow}
          >
            + Add Row
          </button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {metrics.map((metric) =>
            metric.isCustom ? (
              <div
                key={metric.key}
                className="flex items-end gap-2 sm:col-span-2"
              >
                <div className="flex-1">
                  <label className={labelClass}>Custom Row Name</label>
                  <input
                    className={inputClass}
                    autoComplete="off"
                    value={metric.label || ""}
                    onChange={(event) =>
                      updateMetricLabel(metric.key, event.target.value)
                    }
                    placeholder="Name this metric"
                  />
                </div>
                <div className="flex-1">
                  <label className={labelClass}>Value</label>
                  <input
                    className={inputClass}
                    autoComplete="off"
                    value={getMetricEntryValue(metric, dateValue)}
                    onChange={(event) =>
                      updateMetricValue(metric.key, event.target.value)
                    }
                    placeholder="Value for this date"
                  />
                </div>
                <button
                  type="button"
                  className="rounded-xl border-2 border-amber-500 px-3 py-2 text-xs font-semibold text-gray-600 hover:text-amber-700"
                  onClick={() => removeMetricRow(metric.key)}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div key={metric.key}>
                <label className={labelClass}>{metric.label}</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={getMetricEntryValue(metric, dateValue)}
                  onChange={(event) =>
                    updateMetricValue(metric.key, event.target.value)
                  }
                  placeholder="Value for this date"
                />
              </div>
            ),
          )}
        </div>
      </div>
    );
  };

  // Netplus Updates (formType 7) — one card per area (204 / 240 / 241), each
  // with a count for every connection status on the selected day.
  const renderNetplusForm = () => {
    const areas = Array.isArray(formData.areas) ? formData.areas : [];
    const dateValue = toDateInputValue(selectedDate);
    return (
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Month</label>
            <input
              type="month"
              className={inputClass}
              autoComplete="off"
              value={formData.month || reportMonth}
              onChange={(event) => updateField("month", event.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input
              type="date"
              className={inputClass}
              autoComplete="off"
              value={dateValue}
              max={toDateInputValue()}
              min={
                earliestPickableDate
                  ? toDateInputValue(earliestPickableDate)
                  : undefined
              }
              onChange={(event) => {
                if (!event.target.value) return;
                const next = new Date(`${event.target.value}T00:00:00`);
                if (!Number.isNaN(next.getTime())) setSelectedDate(next);
              }}
            />
          </div>
        </div>
        <p className="text-xs text-gray-400">
          Counts below are saved for {formatDateLabel(selectedDate)}. Come back
          each day to add that day's figures — earlier days are kept.
        </p>
        <div className="space-y-4">
          {areas.map((area) => {
            const entry = getNetplusEntry(area, dateValue);
            return (
              <div
                key={area.code}
                className="rounded-2xl border border-gray-200 p-4"
              >
                <div className="mb-3 text-sm font-semibold text-gray-700">
                  Area {area.code}
                </div>
                <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
                  {NETPLUS_STATUS_FIELDS.map(({ key, label }) => (
                    <div key={key}>
                      <label className={labelClass}>{label}</label>
                      <input
                        type="number"
                        min="0"
                        className={inputClass}
                        autoComplete="off"
                        value={entry[key] ?? ""}
                        onChange={(event) =>
                          updateNetplusValue(area.code, key, event.target.value)
                        }
                        placeholder="0"
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Netplus Manager Daily (formType 8) — the manager types today's six figures.
  const renderNetplusMgrForm = () => {
    const dateValue = toDateInputValue(selectedDate);
    const entry = getNetplusMgrEntry(formData.entries, dateValue);
    const rows = withNetplusMgrRunningTotals(formData.entries);
    const upToToday = rows.filter((r) => r.date <= dateValue);
    const running = upToToday.length
      ? upToToday[upToToday.length - 1].running
      : {};
    return (
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Month</label>
            <input
              type="month"
              className={inputClass}
              autoComplete="off"
              value={formData.month || reportMonth}
              onChange={(event) => updateField("month", event.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input
              type="date"
              className={inputClass}
              autoComplete="off"
              value={dateValue}
              max={toDateInputValue()}
              min={
                earliestPickableDate
                  ? toDateInputValue(earliestPickableDate)
                  : undefined
              }
              onChange={(event) => {
                if (!event.target.value) return;
                const next = new Date(`${event.target.value}T00:00:00`);
                if (!Number.isNaN(next.getTime())) setSelectedDate(next);
              }}
            />
          </div>
        </div>
        <p className="text-xs text-gray-400">
          Figures below are saved for {formatDateLabel(selectedDate)}. Enter
          each day's numbers — the running total is added up automatically.
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {NETPLUS_MGR_FIELDS.map(({ key, label, cumulative }) => (
            <div key={key}>
              <label className={labelClass}>{label}</label>
              <input
                className={inputClass}
                autoComplete="off"
                inputMode="numeric"
                placeholder="0"
                value={entry[key] ?? ""}
                onChange={(event) =>
                  updateNetplusMgrValue(key, event.target.value)
                }
              />
              {cumulative && (
                <p className="mt-1 text-xs text-gray-400">
                  Total so far: <b>{running[key] ?? 0}</b>
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Real Estate Sales (4) — manual daily counts (Open Visits / Site Visits /
  // Phone Calls / Sales) for the chosen Working Date. Same store the Detail
  // page's Daily Activity panel writes to.
  const renderDailyActivity = () => {
    const date = toDateInputValue(selectedDate);
    const row = getDailyStat(formData.dailyStats, date);
    const setField = (field, value) =>
      setFormData((prev) => ({
        ...prev,
        dailyStats: upsertDailyStat(prev.dailyStats, date, {
          [field]: value,
        }),
      }));
    return (
      <div className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">
          Daily Activity — {formatDateLabel(selectedDate)}
        </h4>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["openVisits", "Open Visits"],
            ["siteVisits", "Site Visits"],
            ["calls", "Phone Calls"],
            ["sales", "Sales (deals)"],
          ].map(([key, label]) => (
            <div key={key}>
              <label className={labelClass}>{label}</label>
              <input
                className={inputClass}
                autoComplete="off"
                inputMode="text"
                placeholder="Enter here"
                value={row[key] ?? ""}
                onChange={(event) => setField(key, event.target.value)}
              />
            </div>
          ))}
        </div>
        <div>
          <label className={labelClass}>Remarks</label>
          <textarea
            className={inputClass}
            rows={2}
            placeholder="Any remarks for the day"
            value={row.remarks ?? ""}
            onChange={(event) => setField("remarks", event.target.value)}
          />
        </div>
      </div>
    );
  };

  // Real Estate Sales (4) — CP / Customer visitors for the Working Date. Saved
  // with the report (or on their own via Save Visitors) into the employee's
  // separate visitor list, which is downloadable as Excel.
  const renderVisitors = () => {
    const listUrl = `/staff-reporting/visitors${
      visitorEmployeeParam ? `?employeeId=${visitorEmployeeParam}` : ""
    }`;
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">
              CP / Customer Visitors — {formatDateLabel(selectedDate)}
            </h4>
            <p className="text-xs text-gray-400">
              One row per channel partner or customer who visited.
            </p>
          </div>
          <button
            type="button"
            className="rounded-xl border-2 border-emerald-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 transition hover:bg-emerald-50"
            onClick={() => navigate(listUrl)}
          >
            View list / Download Excel →
          </button>
        </div>
        {dayVisitors.length > 0 && (
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
              Already saved for this date · {dayVisitors.length}
            </div>
            <table className="min-w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                {dayVisitors.map((v) => (
                  <tr key={v._id}>
                    <td className="px-4 py-1.5 text-xs font-semibold uppercase text-gray-500">
                      {v.visitorType}
                    </td>
                    <td className="px-4 py-1.5 text-gray-600">
                      {v.visitType || "—"}
                    </td>
                    <td className="px-4 py-1.5 text-gray-800">{v.name}</td>
                    <td className="px-4 py-1.5 text-gray-600">
                      {v.phone || "—"}
                    </td>
                    <td className="px-4 py-1.5 text-gray-600">
                      {v.remarks || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <VisitorRowsEditor
          rows={visitorRows}
          onChange={setVisitorRows}
          onSave={handleSaveVisitors}
          saving={savingVisitors}
        />
      </div>
    );
  };

  // Tele Calling (3) & Real Estate Sales (4) — the Contacts section. The
  // employee's Master Customer roster is shown for reference; the per-day
  // "Called → remark → Save call" flow lives on the report Detail page.
  // "+ Add Contact" seeds brand-new customers that also land in the Master
  // list on save.
  const renderContactBuilder = (description) => {
    const contacts = Array.isArray(formData.contacts) ? formData.contacts : [];
    const roster = Array.isArray(masterCustomers)
      ? masterCustomers.filter((c) => !c.archived)
      : [];
    return (
      <div className="space-y-4">
        {activeReport?._id && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-4">
            <div className="text-sm text-emerald-800">
              This month's sheet exists. Log calls (tick <b>Called</b> → type
              the remark → <b>Save call</b>) and daily activity on the report
              page.
            </div>
            <button
              type="button"
              className="rounded-full bg-emerald-600 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-emerald-500"
              onClick={() => navigate(`/staff-reporting/${activeReport._id}`)}
            >
              Open report to log calls →
            </button>
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">
              Contacts
            </h4>
            <p className="text-xs text-gray-400">{description}</p>
          </div>
          <button
            type="button"
            className="rounded-xl border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600 transition hover:text-amber-700"
            onClick={() =>
              addArrayRow("contacts", () =>
                createContact(toDateInputValue(selectedDate)),
              )
            }
          >
            + Add Contact
          </button>
        </div>

        {roster.length > 0 && (
          <div className="rounded-2xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
              Master Customer roster · {roster.length} — auto-added to every
              report. Tick "Called" &amp; add remarks on the report page.
            </div>
            <div className="max-h-56 overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="px-4 py-1.5 text-left font-medium">
                      Customer
                    </th>
                    <th className="px-4 py-1.5 text-left font-medium">Phone</th>
                    <th className="px-4 py-1.5 text-left font-medium">Lead</th>
                    <th className="px-4 py-1.5 text-left font-medium">Type</th>
                    <th className="px-4 py-1.5 text-left font-medium">
                      Budget
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {roster.map((c) => (
                    <tr key={c.id}>
                      <td className="px-4 py-1.5 text-gray-800">
                        {c.name || "—"}
                      </td>
                      <td className="px-4 py-1.5 text-gray-600">
                        {c.phone || "—"}
                      </td>
                      <td className="px-4 py-1.5 text-gray-600">
                        {c.lead || "—"}
                      </td>
                      <td className="px-4 py-1.5 text-gray-600">
                        {c.propertyType || "—"}
                      </td>
                      <td className="px-4 py-1.5 text-gray-600">
                        {c.budget || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {contacts.length === 0 && roster.length === 0 && (
          <div className="rounded-2xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            No customers yet. Add rows here, or bulk-upload on the Customer
            Database page.
          </div>
        )}
        {contacts.length > 0 && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
            New customers
          </p>
        )}
        {contacts.map((contact, index) => (
          <div
            key={contact.id || index}
            className="grid gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4 sm:grid-cols-2 lg:grid-cols-6"
          >
            {[
              ["customerName", "Customer Name"],
              ["customerPhone", "Customer Phone"],
              ["remarks", "Remarks / Call Log"],
            ].map(([key, ph]) => (
              <input
                key={key}
                className={inputClass}
                autoComplete="off"
                placeholder={ph}
                value={contact[key] || ""}
                onChange={(event) =>
                  updateArrayItem("contacts", index, key, event.target.value)
                }
              />
            ))}
            {[
              ["lead", "Lead", LEAD_SOURCE_OPTIONS],
              ["propertyType", "Type", PROPERTY_TYPE_OPTIONS],
            ].map(([key, label, options]) => (
              <select
                key={key}
                className={inputClass}
                value={contact[key] || ""}
                onChange={(event) =>
                  updateArrayItem("contacts", index, key, event.target.value)
                }
              >
                <option value="">{label}</option>
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ))}
            <input
              className={inputClass}
              autoComplete="off"
              placeholder="Budget"
              value={contact.budget || ""}
              onChange={(event) =>
                updateArrayItem("contacts", index, "budget", event.target.value)
              }
            />
            <button
              type="button"
              className="rounded-xl border-2 border-rose-300 px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50"
              onClick={() => removeArrayRow("contacts", index)}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderAccountantTasks = () => {
    const tasks = Array.isArray(formData.tasks) ? formData.tasks : [];
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">
              Task List
            </h4>
            <p className="text-xs text-gray-400">
              Add every task being tracked; update status as work progresses.
            </p>
          </div>
          <button
            type="button"
            className="rounded-xl bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-950 transition hover:bg-amber-400"
            onClick={() =>
              addArrayRow("tasks", () =>
                createAccountantTask(toDateInputValue(selectedDate)),
              )
            }
          >
            Add Task
          </button>
        </div>
        <div className="space-y-3">
          {tasks.map((task, index) => (
            <div
              key={index}
              className="rounded-2xl border border-gray-200 bg-gray-50 p-4"
            >
              <div className="grid gap-3 md:grid-cols-4">
                <div>
                  <label className={labelClass}>Align Date</label>
                  <input
                    type="date"
                    className={inputClass}
                    autoComplete="off"
                    value={task.assignDate || ""}
                    onChange={(event) =>
                      updateArrayItem(
                        "tasks",
                        index,
                        "assignDate",
                        event.target.value,
                      )
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Work</label>
                  <input
                    className={inputClass}
                    autoComplete="off"
                    value={task.work || ""}
                    onChange={(event) =>
                      updateArrayItem(
                        "tasks",
                        index,
                        "work",
                        event.target.value,
                      )
                    }
                    placeholder="Task description"
                  />
                </div>
                <div>
                  <label className={labelClass}>Due Date</label>
                  <input
                    type="date"
                    className={inputClass}
                    autoComplete="off"
                    value={task.dueDate || ""}
                    onChange={(event) =>
                      updateArrayItem(
                        "tasks",
                        index,
                        "dueDate",
                        event.target.value,
                      )
                    }
                  />
                </div>
                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    className={inputClass}
                    autoComplete="off"
                    value={task.status || "Pending"}
                    onChange={(event) =>
                      updateArrayItem(
                        "tasks",
                        index,
                        "status",
                        event.target.value,
                      )
                    }
                  >
                    <option>Pending</option>
                    <option>Done</option>
                    <option>Under Review</option>
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className={labelClass}>Remarks</label>
                  <input
                    className={inputClass}
                    autoComplete="off"
                    value={task.remarks || ""}
                    onChange={(event) =>
                      updateArrayItem(
                        "tasks",
                        index,
                        "remarks",
                        event.target.value,
                      )
                    }
                    placeholder="Notes / blockers"
                  />
                </div>
              </div>
              <div className="mt-3 text-right">
                <button
                  type="button"
                  className="rounded-xl border-2 border-amber-500 px-3 py-2 text-xs font-semibold text-gray-600 hover:text-amber-700"
                  onClick={() => removeArrayRow("tasks", index)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRevenueTasks = () => {
    const tasks = Array.isArray(formData.tasks) ? formData.tasks : [];
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">
              Task List
            </h4>
            <p className="text-xs text-gray-400">
              Track each parcel/registry task with its own dates, status, and
              assigned technical staff.
            </p>
          </div>
          <button
            type="button"
            className="rounded-xl bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-950 transition hover:bg-amber-400"
            onClick={() =>
              addArrayRow("tasks", () =>
                createRevenueTask(toDateInputValue(selectedDate)),
              )
            }
          >
            Add Task
          </button>
        </div>
        <div className="space-y-3">
          {tasks.map((task, index) => (
            <div
              key={index}
              className="rounded-2xl border border-gray-200 bg-gray-50 p-4"
            >
              <div className="grid gap-3 md:grid-cols-4">
                <div>
                  <label className={labelClass}>Work Assign Date</label>
                  <input
                    type="date"
                    className={inputClass}
                    autoComplete="off"
                    value={task.workAssignDate || ""}
                    onChange={(event) =>
                      updateArrayItem(
                        "tasks",
                        index,
                        "workAssignDate",
                        event.target.value,
                      )
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Work</label>
                  <input
                    className={inputClass}
                    autoComplete="off"
                    value={task.work || ""}
                    onChange={(event) =>
                      updateArrayItem(
                        "tasks",
                        index,
                        "work",
                        event.target.value,
                      )
                    }
                    placeholder="Revenue follow-up, documentation, approvals"
                  />
                </div>
                <div>
                  <label className={labelClass}>Work Due Date</label>
                  <input
                    type="date"
                    className={inputClass}
                    autoComplete="off"
                    value={task.workDueDate || ""}
                    onChange={(event) =>
                      updateArrayItem(
                        "tasks",
                        index,
                        "workDueDate",
                        event.target.value,
                      )
                    }
                  />
                </div>
                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    className={inputClass}
                    autoComplete="off"
                    value={task.status || "Pending"}
                    onChange={(event) =>
                      updateArrayItem(
                        "tasks",
                        index,
                        "status",
                        event.target.value,
                      )
                    }
                  >
                    <option>Pending</option>
                    <option>In Progress</option>
                    <option>Done</option>
                    <option>Under Review</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                      Assigned Technical Staff
                    </label>
                    <label className="flex items-center gap-1 text-[14px] font-medium normal-case text-gray-500">
                      <input
                        style={{
                          width: "18px",
                          height: "18px",
                          backgroundColor: "white",
                        }}
                        type="checkbox"
                        class="accent-purple-500/25"
                        checked={task.technicalStaffEnabled !== false}
                        onChange={(event) => {
                          const enabled = event.target.checked;
                          updateArrayItem(
                            "tasks",
                            index,
                            "technicalStaffEnabled",
                            enabled,
                          );
                          if (!enabled) {
                            updateArrayItem(
                              "tasks",
                              index,
                              "assignedTechnicalStaff",
                              "",
                            );
                          }
                        }}
                      />
                      Required
                    </label>
                  </div>
                  {task.technicalStaffEnabled !== false && (
                    <select
                      className={inputClass}
                      autoComplete="off"
                      value={task.assignedTechnicalStaff || ""}
                      onChange={(event) =>
                        updateArrayItem(
                          "tasks",
                          index,
                          "assignedTechnicalStaff",
                          event.target.value,
                        )
                      }
                    >
                      <option value="">Select technical staff</option>
                      {technicalStaff.map((person) => (
                        <option key={person._id} value={person._id}>
                          {person.name} - {formatRoleLabel(person.role)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Remarks</label>
                  <input
                    className={inputClass}
                    autoComplete="off"
                    value={task.remarks || ""}
                    onChange={(event) =>
                      updateArrayItem(
                        "tasks",
                        index,
                        "remarks",
                        event.target.value,
                      )
                    }
                    placeholder="Dependency / follow-up note"
                  />
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-gray-200 bg-white p-3">
                <div className="flex items-center justify-between gap-3">
                  <label className={labelClass}>
                    Sub Works under &quot;{task.work || "this work"}&quot;
                  </label>
                  <button
                    type="button"
                    className="rounded-lg bg-amber-500 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-950 transition hover:bg-amber-400"
                    onClick={() => addTaskSubWork(index)}
                  >
                    Add Sub-work
                  </button>
                </div>
                {(Array.isArray(task.subWorks) ? task.subWorks : []).length ? (
                  <div className="mt-3 space-y-2">
                    {task.subWorks.map((subWork, subIndex) => (
                      <div
                        key={subIndex}
                        className="grid gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3 md:grid-cols-4"
                      >
                        <div className="md:col-span-2">
                          <label className={labelClass}>Sub-work</label>
                          <input
                            className={inputClass}
                            autoComplete="off"
                            value={subWork.subWorkName || ""}
                            onChange={(event) =>
                              updateTaskSubWork(
                                index,
                                subIndex,
                                "subWorkName",
                                event.target.value,
                              )
                            }
                            placeholder="Sub-work name"
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Start</label>
                          <input
                            type="date"
                            className={inputClass}
                            autoComplete="off"
                            value={subWork.workStartDate || ""}
                            onChange={(event) =>
                              updateTaskSubWork(
                                index,
                                subIndex,
                                "workStartDate",
                                event.target.value,
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Extend</label>
                          <input
                            type="date"
                            className={inputClass}
                            autoComplete="off"
                            value={subWork.workExtendDate || ""}
                            onChange={(event) =>
                              updateTaskSubWork(
                                index,
                                subIndex,
                                "workExtendDate",
                                event.target.value,
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Status</label>
                          <select
                            className={inputClass}
                            autoComplete="off"
                            value={subWork.status || "Pending"}
                            onChange={(event) =>
                              updateTaskSubWork(
                                index,
                                subIndex,
                                "status",
                                event.target.value,
                              )
                            }
                          >
                            <option>Pending</option>
                            <option>In Progress</option>
                            <option>Complete</option>
                          </select>
                        </div>
                        <div className="text-right md:col-span-4">
                          <button
                            type="button"
                            className="rounded-lg border-2 border-amber-500 px-3 py-1.5 text-[11px] font-semibold text-gray-600 hover:text-amber-700"
                            onClick={() => removeTaskSubWork(index, subIndex)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-gray-400">
                    No sub-works added yet.
                  </p>
                )}
              </div>

              <div className="mt-3 text-right">
                <button
                  type="button"
                  className="rounded-xl border-2 border-amber-500 px-3 py-2 text-xs font-semibold text-gray-600 hover:text-amber-700"
                  onClick={() => removeArrayRow("tasks", index)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderForm = () => {
    switch (formType) {
      case 1:
        return renderFieldJournal();
      case 2:
        return renderMetricsForm(
          "Daily Metrics",
          "Fixed metric rows for Office Staff / Personal Assistant.",
        );
      case 3:
        return (
          <div className="space-y-8">
            {renderMetricsForm(
              "Daily Calling Metrics",
              "Netplus / New Leads / Hotel / CP / Client calling counts per day.",
            )}
            {renderContactBuilder(
              "Customer / channel-partner rows this telecaller will be calling.",
            )}
          </div>
        );
      case 4:
        return (
          <div className="space-y-8">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Month</label>
                <input
                  type="month"
                  className={inputClass}
                  autoComplete="off"
                  value={formData.month || reportMonth}
                  onChange={(event) => updateField("month", event.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Date</label>
                <input
                  type="date"
                  className={inputClass}
                  autoComplete="off"
                  value={toDateInputValue(selectedDate)}
                  max={toDateInputValue()}
                  min={
                    earliestPickableDate
                      ? toDateInputValue(earliestPickableDate)
                      : undefined
                  }
                  onChange={(event) => {
                    if (!event.target.value) return;
                    const next = new Date(`${event.target.value}T00:00:00`);
                    if (!Number.isNaN(next.getTime())) setSelectedDate(next);
                  }}
                />
              </div>
            </div>
            {renderDailyActivity()}
            {renderVisitors()}
            {renderContactBuilder(
              "Customer / channel-partner rows for visits and calls.",
            )}
          </div>
        );
      case 5:
        return renderAccountantTasks();
      case 6:
        return renderRevenueTasks();
      case 7:
        return renderNetplusForm();
      case 8:
        return renderNetplusMgrForm();
      default:
        return (
          <div className="rounded-2xl border border-dashed border-gray-300 p-6 text-center text-gray-500">
            Select an active employee with a supported role to load a reporting
            form.
          </div>
        );
    }
  };

  return (
    <>
      <PageTitle activeMenu="Staff Reporting" motherMenu="Workforce" />

      <div className="space-y-6">
        <section className={`${darkPanel} relative overflow-hidden p-6`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(169,126,8,0.10),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(226,232,240,0.6),_transparent_40%)]" />
          <div className="relative grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Live sheet editor
              </div>
              <h1 className="mt-4 text-3xl font-semibold text-gray-900 md:text-4xl">
                Staff Reporting
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-500">
                Default the date to today, choose an active employee, and the
                module will load the exact form for their role. Updates merge
                into the same active sheet until the work is completed.
              </p>
            </div>

            <div className="grid gap-3 rounded-3xl border border-gray-200 bg-gray-50 p-4">
              <div>
                <label className={labelClass}>Working Date</label>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => date && setSelectedDate(date)}
                  className={inputClass}
                  autoComplete="off"
                  dateFormat="dd/MM/yyyy"
                  maxDate={new Date()}
                  minDate={onBehalfMode ? undefined : earliestPickableDate}
                  filterDate={
                    isSuperAdmin || onBehalfMode
                      ? undefined
                      : (day) => {
                          const yesterday = new Date();
                          yesterday.setDate(yesterday.getDate() - 1);
                          const key = toDateInputValue(day);
                          return (
                            key === toDateInputValue(new Date()) ||
                            key === toDateInputValue(yesterday) ||
                            grantedDayKeys.has(key)
                          );
                        }
                  }
                />
                {!isSuperAdmin && (
                  <p className="mt-1 text-xs text-gray-400">
                    You can add reports for today and yesterday.
                    {grantedDayKeys.size > 0
                      ? ` Past dates unlocked for you: ${[...grantedDayKeys].sort().join(", ")}.`
                      : " An earlier date needs a super-admin's permission."}
                  </p>
                )}
              </div>
              <div>
                <label className={labelClass}>Employee</label>
                <select
                  className={inputClass}
                  autoComplete="off"
                  value={selectedEmployeeId}
                  onChange={(event) => handleEmployeeChange(event.target.value)}
                  disabled={staffLoading}
                >
                  <option value="">
                    {staffLoading ? "Loading employees…" : "Select employee"}
                  </option>
                  {employees.map((employee) => (
                    <option key={employee._id} value={employee._id}>
                      {employee.name} - {formatRoleLabel(employee.role)}
                    </option>
                  ))}
                </select>
              </div>
              {onBehalfMode && (
                <div className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-xs text-violet-800">
                  You are adding a <b>new</b> report on behalf of{" "}
                  <b>{selectedEmployee?.name}</b>. It will be marked{" "}
                  <b>"Added by {currentUser?.name || "you"}"</b>. Existing
                  reports of team members can only be edited by the member.
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 text-center text-xs text-gray-600">
                <div className="rounded-2xl border border-gray-200 bg-white px-3 py-3">
                  <div className="text-gray-400">Form</div>
                  <div className="mt-1 text-lg font-semibold text-gray-900">
                    {formType || "-"}
                  </div>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white px-3 py-3">
                  <div className="text-gray-400">Completion</div>
                  <div className="mt-1 text-lg font-semibold text-gray-900">
                    {completion}%
                  </div>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white px-3 py-3">
                  <div className="text-gray-400">Mode</div>
                  <div className="mt-1 text-lg font-semibold text-gray-900">
                    {activeReport?._id ? "Update" : "Create"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <form
          onSubmit={handleSubmit}
          onKeyDown={handleFormKeyDown}
          className={`${darkPanel} p-5 md:p-6`}
        >
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedEmployee
                  ? `${selectedEmployee.name} - ${formatRoleLabel(selectedEmployee.role)}`
                  : "Select an employee to start"}
              </h2>
              <p className="text-sm text-gray-500">
                {activeReport?._id
                  ? `Last edited ${formatDateLabel(activeReport.lastEditedAt || activeReport.updatedAt)}`
                  : reportFetching
                    ? "Looking for an active sheet for the selected employee and period..."
                    : "A new sheet will be created if no active record exists for this period."}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {activeReport?._id && (
                <button
                  type="button"
                  disabled={saving || !formType}
                  onClick={handleStartNewSheet}
                  className="rounded-full border-2 border-amber-500 px-5 py-3 text-xs font-semibold uppercase tracking-[0.22em] text-gray-600 transition hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  New Sheet
                </button>
              )}
              <button
                type="submit"
                disabled={saving || !formType}
                className="rounded-full bg-amber-500 px-5 py-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving
                  ? "Saving..."
                  : activeReport?._id
                    ? "Update Sheet"
                    : "Create Sheet"}
              </button>
            </div>
          </div>

          {renderForm()}
        </form>
      </div>
    </>
  );
};

export default CreateStaffReporting;
